import config from 'react-global-configuration';

import type { AppOptions, SiteDomain } from '../types';

export let APP_ENV = process.env.REACT_APP_ENV;
export const __DEBUG__ = APP_ENV !== 'production';
export const USE_SENTRY = process.env.REACT_APP_SENTRY_ENABLED;
export const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

export const DEFAULT_LANGUAGE = 'nl';
export const SITE_NAME = '<SITE NAME>';
export let CURRENT_DOMAIN: SiteDomain = 'regenboog';

export const MULTILINGUAL = true;
export const LANGUAGE_NL = 'nl';
export const LANGUAGE_EN = 'en';

export const SUPPORTED_LANGUAGES = [LANGUAGE_NL, LANGUAGE_EN];

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

export const IS_SERVER = !(
  typeof window !== 'undefined'
  && window.document
  && window.document.createElement
);
export const IS_CLIENT = !IS_SERVER;

export let API_URL = process.env.REACT_APP_API_URL || process.env.API_URL || 'https://www.deregenboog.org';

// Searchkit host url.
export let SEARCHKIT_HOST_URL = process.env.REACT_APP_SEARCHKIT_HOST_URL || '/api-proxy/elasticsearch-proxy?_api_proxy_uri=';

export const DOMAIN_REGENBOOG = 'regenboog';
export const DOMAIN_VRIENDEN_REGENBOOG = 'vrienden_regenboog';
export const DOMAIN_ANNUAL_REPORT_REGENBOOG = 'annual_report_regenboog';
export const DOMAIN_BUURTBOERDERIJ = 'buurtboerderij';
export const DOMAIN_AMSTERDAMUNDERGROUND = 'amsterdamunderground';

export const DOMAIN_ACCESS_REGENBOOG = 'deregenboog_org';
export const DOMAIN_ACCESS_VRIENDEN_REGENBOOG = 'vriendenvanderegenboog_org';
export const DOMAIN_ACCESS_ANNUAL_REPORT_REGENBOOG = 'jaarverslag_deregenboog_org';
export const DOMAIN_ACCESS_BUURTBOERDERIJ = 'buurtboerderij_nl';
export const DOMAIN_ACCESS_AMSTERDAMUNDERGROUND = 'amsterdamunderground_org';

export const HOSTNAME_ANNUAL_REPORT = 'https://jaarverslag.deregenboog.org';

let configSet = false;

export const NAVIGATION_BAR_HEIGHT_MOBILE = 65;
export const NAVIGATION_BAR_HEIGHT_TABLET = 80;
export const NAVIGATION_BAR_HEIGHT_NORMAL = 120;

function getCurrentDomain() {
  if (API_URL.match(/jaarverslag/) !== null) {
    return DOMAIN_ANNUAL_REPORT_REGENBOOG;
  }

  if (API_URL.match(/vriendenvanderegenboog/) !== null) {
    return DOMAIN_VRIENDEN_REGENBOOG;
  }

  if (API_URL.match(/buurtboerderij/) !== null) {
    return DOMAIN_BUURTBOERDERIJ;
  }

  if (API_URL.match(/amsterdamunderground/) !== null) {
    return DOMAIN_AMSTERDAMUNDERGROUND;
  }

  if (API_URL.match(/deregenboog/) !== null) {
    return DOMAIN_REGENBOOG;
  }

  return CURRENT_DOMAIN;
}

const currentDomain = getCurrentDomain();

// Use config to share static configuration between components and packages.
config.set({
  'SITE_NAME': SITE_NAME,
  'CURRENT_DOMAIN': currentDomain,
  'MULTILINGUAL': MULTILINGUAL,
  'DEFAULT_LANGUAGE': DEFAULT_LANGUAGE,
  'SUPPORTED_LANGUAGES': SUPPORTED_LANGUAGES,
  'API_URL': API_URL,
  'RECAPTCHA_SITE_KEY': RECAPTCHA_SITE_KEY,
}, { freeze: false });

export let APP_OPTIONS: AppOptions = {
  apiUrl: API_URL,
  element: '',
  parameters: {
    env: 'production',
    blockIdBuurtboerderijAboutUs: 8,
    blockIdBuurtboerderijFooterContact: 9,
    blockIdBuurtboerderijFooterOpeningHours: 10,
    blockIdAmsterdamundergroundFooterContact: 11,
    searchkitIndexAllContent: 'elasticsearch_index_regenboog_production_content_all',
  }
};

// The value is where the breakpoint starts (mobile-first).
// These values are taken from '/src/src/styles/assets/_variables.scss'.
export const SCREEN_MOBILE = 0;
export const SCREEN_TABLET = 768;
export const SCREEN_NORMAL = 992;
export const SCREEN_WIDE = 1240;
export const SCREEN_HUGE = 2000;

// Placeholder image sources
export const CONTENT_IMAGE_PLACEHOLDER = '/sites/default/files/2022-09/placeholder.jpg';
export const CONTENT_IMAGE_VERTICAL_PLACEHOLDER = '/sites/default/files/2022-09/placeholder_vertical.jpg';

// The block definitions
export const BLOCK_CONTACT_BLOCK_ID = 1;
export const BLOCK_VISITING_ADDRESS_BLOCK_ID = 2;
export const CONTACT_BLOCK_TITLE_IDENTIFIER = 'Contact';
export const BLOCK_PINNED_NOTIFICATION_BLOCK_ID = 3;
export const BLOCK_VACANCY_CTA_BLOCK_ID = 4;
export const BLOCK_NEWSLETTER_SUBSCRIBE_BLOCK_ID = 5;
export const BLOCK_DONATION_BLOCK_ID = 6;
export const BLOCK_DOWNLOAD_BLOCK_ID = 7;

// Content types
export const CONTENT_TYPE_LANDING_PAGE = 'landing_page';
export const CONTENT_TYPE_ACTIVITY = 'activity';
export const CONTENT_TYPE_ARTICLE = 'article';

export const VACANCY_TYPES = {
  Volunteer: 'volunteer_work',
  Paid: 'payed_employment',
  Internship: 'internship',
  DaytimeActivity: 'vacancies_daytime_activity',
};

export const VACANCY_EMPLOYMENT_TYPES = {
  'volunteer_work': 'VOLUNTEER',
  'payed_employment': ['FULL_TIME', 'PART_TIME'],
  'internship': 'INTERN',
  'vacancies_daytime_activity': 'PER_DIEM',
  '': 'OTHER'
};

export const VOLUNTEER_TAXONOMY_TID = 195;

export const ARTICLE_TYPES = {
  News: 'Nieuws',
  Stories: 'Verhalen',
};

export const OVERVIEW_REDIRECT = {
  locations: {
    nl: '/nl/overzicht-locaties',
    en: '/en/locations',
  },
  news: {
    nl: '/nl/nieuws',
    en: '/en/news',
  },
  stories: {
    nl: '/nl/verhalen',
    en: '/en/stories',
  },
  vacancies: {
    volunteer_work: {
      nl: '/nl/vrijwilligerswerk-amsterdam',
      en: '/en/vacancies-for-volunteers',
    },
    payed_employment: {
      nl: '/nl/vacatures-en-stages',
      en: '/en/vacancies',
    },
    internship: {
      nl: '/nl/vacatures-en-stages',
      en: '/en/vacancies',
    },
    vacancies_daytime_activity: {
      nl: '/nl/vacatures-voor-dagbesteding',
      en: '/en/vacancies-for-daytime-activities',
    }
  },
  projects: {
    nl: '/nl/projecten',
    en: '/en/projects',
  },
  activities: {
    participant: {
      nl: '/nl/activiteiten-trainingen',
      en: '/en/activities',
    },
    volunteer: {
      nl: '/nl/activiteiten-voor-regenboog-vrijwilligers',
      en: '/en/activities-for-regenboog-volunteers',
    },
  }
};

export const APPLY_FORM = {
  volunteer_work: {
    nl: '/nl/ja-ik-word-vrijwilliger',
    en: '/en/ja-ik-word-vrijwilliger',
  },
  payed_employment: {
    nl: '/nl/solliciteer',
    en: '/en/solliciteer',
  },
  internship: {
    nl: '/nl/stage',
    en: '/en/stage',
  },
  vacancies_daytime_activity: {
    nl: '/nl/ik-wil-aan-de-slag',
    en: '/en/ik-wil-aan-de-slag',
  }
};

export const WEBFORM_APPLY_FOR_ACTIVITY = 'apply_for_activity';

export const NODE_TYPE_ANNUAL_REPORT_LANDING_PAGE = 'annual_report_landing_page';
export const NODE_TYPE_ACTIVITY_TARGET_AUDIENCE = {
  PARTICIPANT: 'participant',
  VOLUNTEER: 'volunteer',
};

export const PARAGRAPH_VIEW_MODE_DEFAULT = 'default';
export const PARAGRAPH_VIEW_MODE_FULLSCREEN = 'fullscreen';
export const PARAGRAPH_VIEW_MODE_HOMEPAGE = 'homepage';
export const PARAGRAPH_VIEW_MODE_CAMPAIGN_COLOR_THEME = 'campaign';

export const webformAnalyticsFields = [
  'kies_een_project',
  'ik_wil_de_digitale_nieuwsbrief_ontvangen_',
  'ik_wil_het_kwartaalblad_meeleven_per_mail_ontvangen_',
  'ik_wil_het_kwartaalblad_meeleven_per_post_ontvangen_',
  'ik_wil_telefonisch_op_de_hoogte_worden_gehouden_van_het_werk_en_',
  'ik_wil_meewerken_in_een_team',
  'ik_wil_hulp_geven_bij_',
  'meewerken_in_een_team',
  'hoe_ken_je_de_regenboog_',
];

export const ZENCHEF_CLASSES = {
  OPEN: 'zc-action-open',
  CLOSE: 'zc-action-close',
};

export function saveConstants(options: AppOptions) {
  APP_OPTIONS = options;

  if (options.parameters.env) {
    APP_ENV = options.parameters.env;
  }

  if (options.apiUrl) {
    API_URL = options.apiUrl;
  }

  if (APP_ENV === 'development') {
    // Use proxy in dev to prevent CORS issues.
    API_URL = 'http://localhost:3000/api';
    SEARCHKIT_HOST_URL = 'http://localhost:3000/search';
  }

  const currentDomain = getCurrentDomain();

  // Update the config.
  config.set({
    'SITE_NAME': SITE_NAME,
    'CURRENT_DOMAIN': currentDomain,
    'MULTILINGUAL': MULTILINGUAL,
    'DEFAULT_LANGUAGE': DEFAULT_LANGUAGE,
    'SUPPORTED_LANGUAGES': SUPPORTED_LANGUAGES,
    'API_URL': API_URL,
    'RECAPTCHA_SITE_KEY': RECAPTCHA_SITE_KEY,
  });
}
